import "../TestEntry.scss";

import { Grid, Stack } from "@mantine/core";
import { useEffect, useState } from "react";

import { EyeDisplacement } from "types/testTypes";
import GazeTrackMap from "../GazeTrackMap";
import SimpleTestMap from "../maps/SimpleTestMap";
import TestResultSheet from "./TestResultSheet";
import { useTranslation } from "react-i18next";
import { computeZoneStats, getZoneSymbolKind } from "@utils/TestUtils";

export default function SimpleTestEntry(props) {
  const { t } = useTranslation();
  const [isInitialized, setIsInitialized] = useState(false);
  const [gazeTrackingData, setGazeTrackingData] = useState(null);
  const [warnings] = useState([]);
  const [pointsSeen, setPointsSeen] = useState(0);
  const [pointsMissed, setPointsMissed] = useState(0);
  const [efficiencyScore, setEfficiencyScore] = useState(0);
  const [values, setValues] = useState([]);
  const [displacements, setDisplacements] = useState<EyeDisplacement[]>(null);
  const result = props.entry as any;

  useEffect(() => {
    if (result?.displacementList) {
      setDisplacements(result.displacementList);
    }
  }, [result]);

  useEffect(() => {
    setIsInitialized(true);
    if (props.entry.gaze && props.entry.gaze.length > 0) {
      addTimeValue(props.entry.startDate, props.entry.finishDate, props.entry.gaze);
    }
  }, []);

  function addTimeValue(initDate, endDate, valueArray) {
    const timeSeriesData = [];
    const startTime = new Date(initDate).getTime();
    const endTime = new Date(endDate).getTime();
    const totalDuration = endTime - startTime;

    const timeInterval = valueArray.length > 1 ? totalDuration / (valueArray.length - 1) : 0;

    valueArray.forEach((value, index) => {
      const timestamp = startTime + index * timeInterval;
      timeSeriesData.push([timestamp, value]);
    });

    setGazeTrackingData(timeSeriesData);
  }

  useEffect(() => {
    if (props.entry) {
      const values = [];
      Object.keys(props.entry.points).forEach((key) => values.push(props.entry.points[key]));
      const kinds = values.map((v) => getZoneSymbolKind(v, props.entry.testBehaviour));
      const { pointsSeen, pointsMissed, efficiencyScore } = computeZoneStats(kinds);
      setPointsSeen(pointsSeen);
      setPointsMissed(pointsMissed);
      setEfficiencyScore(efficiencyScore);
      setValues(values);
    }
  }, [isInitialized]);

  function getTestContent() {
    return (
      <Grid>
        <Grid.Col span={8}>
          <SimpleTestMap
            values={values}
            testType={props.entry.testType}
            eye={props.entry.eye}
            testBehaviour={props.entry.testBehaviour}
          />
        </Grid.Col>
        <Grid.Col span={4}>
          <div className={"stat-symbols-container"}>
            {warnings.length > 0 ? (
              <>
                {warnings.map((w, index) => {
                  return (
                    <div key={`${w}-${index}`} className={"stat-symbols-container"}>
                      <span className={"warning-text"}>{t(w)}</span>
                    </div>
                  );
                })}
              </>
            ) : null}
          </div>

          <div className={"stat-symbols-container"}>
            <div>
              <span className={"stat-text"}>{t("pointsSeen")}</span>
              <span className={"stat-text"}>{pointsSeen}</span>
            </div>
            <div>
              <span className={"stat-text"}>{t("pointsMissed")}</span>
              <span className={"stat-text"}>{pointsMissed}</span>
            </div>
            <div>
              <span className={"stat-text"}>{t("efficiencyScore")}</span>
              <span className={"stat-text"}>{efficiencyScore}%</span>
            </div>
          </div>
        </Grid.Col>
        <Grid.Col span={12}>
          <Stack>
            {gazeTrackingData && <GazeTrackMap data={gazeTrackingData} />}
            {displacements && !gazeTrackingData && <GazeTrackMap data={displacements} />}
          </Stack>
        </Grid.Col>
      </Grid>
    );
  }

  return (
    <TestResultSheet
      entry={props.entry}
      getResultSheetContent={() => getTestContent()}
      onPatientChanged={props.onPatientChanged}
    />
  );
}
